import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Button,
  Alert,
  Statistic,
  Space,
  Typography,
  Divider,
  notification,
} from "antd";
import {
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import SupportCoursesService from "services/SupportCoursesService";
import { getSupportCourse } from "store/slices/supportCoursesSlice";

const { Text } = Typography;
const { Option } = Select;

const AddSessionModal = () => {
  const { t } = useTranslation();
  const [form] = Form?.useForm();
  const dispatch = useDispatch();

  // États locaux
  const [loading, setLoading] = useState(false);
  const [additionalSessions, setAdditionalSessions] = useState(1);
  const [newLearningType, setNewLearningType] = useState(null);
  const [calculatedAmount, setCalculatedAmount] = useState(0);
  const [canChangeLearningType, setCanChangeLearningType] = useState(true);
  const [warningMessage, setWarningMessage] = useState("");
  const [successDetails, setSuccessDetails] = useState(null);

  // Sélecteurs Redux
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { supportCourse } = useSelector((state) => state?.supportCourses);

  // État du modal
  const open = isModalOpen && modalType === "add_session_to_student";
  const { studentSupportCourse, supportCourseId } = modalProps || {};

  // Calculer le montant des sessions supplémentaires
  const calculateAdditionalAmount = (sessions, learningType) => {
    if (!supportCourse || !sessions) return 0;
    const currentLearningType =
      learningType || studentSupportCourse?.learningType;
    const pricePerSession =
      currentLearningType === "particulier" && supportCourse?.individualPrice
        ? supportCourse?.individualPrice
        : supportCourse?.price || 0;
    return pricePerSession * sessions;
  };

  // Mettre à jour le montant calculé
  useEffect(() => {
    setCalculatedAmount(
      calculateAdditionalAmount(additionalSessions, newLearningType)
    );
  }, [
    additionalSessions,
    newLearningType,
    supportCourse,
    studentSupportCourse,
  ]);

  // Vérifier si le changement de type d'apprentissage est possible
  useEffect(() => {
    if (studentSupportCourse && open) {
      const hasUncompletedSessions = studentSupportCourse?.numberOfSessions > 0;
      if (hasUncompletedSessions) {
        setCanChangeLearningType(false);
        setWarningMessage(
          t(
            "supportCourse.addSession.warning.hasUncompletedSessions",
            "Impossible de changer le type d'apprentissage car l'étudiant a des séances non terminées"
          )
        );
      } else {
        setCanChangeLearningType(true);
        setWarningMessage("");
      }
      // Initialiser les valeurs du formulaire
      form?.setFieldsValue({
        additionalSessions: 1,
        newLearningType: studentSupportCourse?.learningType,
      });
      setNewLearningType(studentSupportCourse?.learningType);
      setAdditionalSessions(1);
      setSuccessDetails(null);
    }
  }, [studentSupportCourse, open, form, t]);

  // Gérer la soumission du formulaire
  const onSubmit = async (values) => {
    try {
      setLoading(true);
      setSuccessDetails(null);
      console?.log("SSC ID: ", studentSupportCourse)
      const requestData = {
        studentSupportCourseId: studentSupportCourse?.studentSupportCourseId?._id,
        additionalSessions: values?.additionalSessions,
      };
      // Respecter le backend : n'envoyer newLearningType que si différent
      if (
        values?.newLearningType &&
        values?.newLearningType !== studentSupportCourse?.learningType
      ) {
        requestData.newLearningType = values?.newLearningType;
      }
      console?.log("request Data : ", requestData)
      const response =
        await SupportCoursesService?.addSessionsToStudentSupportCourse(
          requestData
        );

      if (response?.data?.status === "success") {
        setSuccessDetails(response?.data?.data);
        notification?.success({
          message: t(
            "supportCourse.addSession.success",
            "Sessions ajoutées avec succès"
          ),
          description: response?.data?.message,
          duration: 4,
        });
        // Recharger les données du cours de soutien
        dispatch(getSupportCourse(supportCourseId));
      }
    } catch (error) {
      notification?.error({
        message: t("supportCourse.addSession.error", "Erreur lors de l'ajout"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("common.error.generic", "Une erreur est survenue"),
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fermer le modal
  const onClose = () => {
    form?.resetFields();
    setAdditionalSessions(1);
    setNewLearningType(null);
    setCalculatedAmount(0);
    setWarningMessage("");
    setSuccessDetails(null);
    dispatch(closeModal());
  };

  // Obtenir les types d'apprentissage disponibles
  const getAvailableLearningTypes = () => {
    if (!supportCourse?.state) return [];
    return supportCourse?.state?.filter((type) =>
      ["particulier", "miniGroupe", "groupe"]?.includes(type)
    );
  };

  const availableLearningTypes = getAvailableLearningTypes();

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined style={{ color: "#1890ff" }} />
          <span>
            {t("supportCourse.addSession.title", "Ajouter des séances")}
          </span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={() => form?.submit()}
      confirmLoading={loading}
      width={600}
      destroyOnClose
      footer={
        !successDetails && [
          <Button key="cancel" onClick={onClose}>
            {t("common.cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form?.submit()}
          >
            {t("supportCourse.addSession.confirm", "Ajouter")}
          </Button>,
        ]
      }
    >
      {studentSupportCourse && supportCourse && !successDetails && (
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues?.additionalSessions) {
              setAdditionalSessions(changedValues?.additionalSessions);
            }
            if (changedValues?.newLearningType) {
              setNewLearningType(changedValues?.newLearningType);
            }
          }}
        >
          <Alert
            message={t(
              "supportCourse.addSession.currentInfo",
              "Informations actuelles"
            )}
            description={
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>
                  <strong>{t("common.student", "Étudiant")}:</strong>{" "}
                  {studentSupportCourse?.studentId?.fullName || "N/A"}
                </Text>
                <Text>
                  <strong>
                    {t(
                      "supportCourse.addSession.currentSessions",
                      "Séances actuelles"
                    )}
                    :
                  </strong>{" "}
                  {studentSupportCourse?.studentSupportCourseId?.numberOfSessions}
                </Text>
                <Text>
                  <strong>
                    {t(
                      "students.form.label.learningType",
                      "Type d'apprentissage"
                    )}
                    :
                  </strong>{" "}
                  {t(
                    `students.form.option.${studentSupportCourse?.studentSupportCourseId?.learningType
                      ?.replace(/\s+/g, "")?.toLowerCase()}`,
                    studentSupportCourse?.studentSupportCourseId?.learningType
                  )}
                </Text>
                <Text>
                  <strong>
                    {t(
                      "supportCourse.student.add.form.learningState",
                      "Lieu d'apprentissage"
                    )}
                    :
                  </strong>{" "}
                  {t(
                    `supportCourse.student.learningState.${studentSupportCourse?.studentSupportCourseId?.learningState}`,
                    studentSupportCourse?.studentSupportCourseId?.learningState
                  )}
                </Text>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {!canChangeLearningType && warningMessage && (
            <Alert
              message={t("supportCourse.addSession.warning", "Attention")}
              description={warningMessage}
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          <Form.Item
            name="additionalSessions"
            label={t(
              "supportCourse.addSession.additionalSessions",
              "Séances supplémentaires"
            )}
            rules={[
              {
                required: true,
                message: t(
                  "supportCourse.addSession.error.sessionsRequired",
                  "Veuillez saisir le nombre de séances"
                ),
              },
              {
                type: "number",
                min: 1,
                message: t(
                  "supportCourse.addSession.error.sessionsMin",
                  "Le nombre de séances doit être au moins 1"
                ),
              },
            ]}
            initialValue={1}
          >
            <InputNumber
              min={1}
              max={50}
              style={{ width: "100%" }}
              addonBefore={<CalendarOutlined />}
              addonAfter={t(
                "supportCourse.student.add.form.sessions",
                "séances"
              )}
              placeholder={t(
                "supportCourse.addSession.sessionsPlaceholder",
                "Nombre de séances"
              )}
            />
          </Form.Item>

          <Form.Item
            name="newLearningType"
            label={t(
              "students.form.label.learningType",
              "Type d'apprentissage"
            )}
            help={
              !canChangeLearningType
                ? t(
                    "supportCourse.addSession.learningTypeDisabled",
                    "Le type d'apprentissage ne peut pas être changé à cause de séances ou groupe en cours"
                  )
                : t(
                    "supportCourse.addSession.learningTypeHelp",
                    "Vous pouvez changer le type d'apprentissage lors de l'ajout de séances"
                  )
            }
          >
            <Select
              disabled={!canChangeLearningType}
              placeholder={t(
                "students.form.label.learningType.placeholder",
                "Sélectionner le type"
              )}
            >
              {availableLearningTypes?.map((type) => (
                <Option key={type} value={type}>
                  {t(
                    `students.form.option.${type?.replace(/\s+/g, "")?.toLowerCase()}`,
                    type
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left" plain>
            <Space>
              <DollarOutlined style={{ color: "#1890ff" }} />
              {t(
                "supportCourse.addSession.paymentCalculation",
                "Calcul du paiement"
              )}
            </Space>
          </Divider>

          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>
                {t(
                  "supportCourse.addSession.pricePerSession",
                  "Prix par séance"
                )}
                :
              </Text>
              <br />
              <Text>
                {(newLearningType === "particulier" &&
                supportCourse?.individualPrice ? supportCourse?.individualPrice : supportCourse?.price || 0)?.toLocaleString()}{" "}
                DZD
              </Text>
            </div>
            <div>
              <Text strong>
                {t(
                  "supportCourse.addSession.numberOfSessions",
                  "Nombre de séances"
                )}
                :
              </Text>
              <br />
              <Text>{additionalSessions}</Text>
            </div>
            <Divider style={{ margin: "12px 0" }} />
            {calculatedAmount > 0 ? (
              <Statistic
                title={t(
                  "supportCourse.addSession.totalAmount",
                  "Montant total"
                )}
                value={calculatedAmount}
                precision={0}
                prefix={<DollarOutlined />}
                suffix="DZD"
                valueStyle={{ color: "#1890ff", fontWeight: "bold" }}
              />
            ) : (
              <Alert
                message={t(
                  "supportCourse.addSession.noPrices",
                  "Aucun prix défini"
                )}
                description={t(
                  "supportCourse.addSession.noPricesDesc",
                  "Aucun prix n'est défini pour ce cours de soutien"
                )}
                type="warning"
                showIcon
              />
            )}
            <Alert
              message={t(
                "supportCourse.addSession.paymentNote",
                "Note de paiement"
              )}
              description={t(
                "supportCourse.addSession.paymentNoteDesc",
                "Le paiement sera automatiquement ajouté à l'existant et le salaire du formateur mis à jour."
              )}
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </Space>
        </Form>
      )}
      {/* Affichage du retour détaillé après succès */}
      {successDetails && (
        <Alert
          type="success"
          showIcon
          message={t(
            "supportCourse.addSession.successDetails",
            "Ajout effectué"
          )}
          description={
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.previousSessions",
                    "Séances avant"
                  )}
                  :
                </strong>{" "}
                {successDetails?.studentSupportCourse?.previousSessionsCount}
              </Text>
              <Text>
                <strong>
                  {t("supportCourse.addSession.newSessions", "Séances après")}:
                </strong>{" "}
                {successDetails?.studentSupportCourse?.newSessionsCount}
              </Text>
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.previousLearningType",
                    "Type avant"
                  )}
                  :
                </strong>{" "}
                {t(
                  `students.form.option.${successDetails?.studentSupportCourse?.previousLearningType
                    ?.replace(/\s+/g, "")?.toLowerCase()}`,
                  successDetails?.studentSupportCourse?.previousLearningType
                )}
              </Text>
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.currentLearningType",
                    "Type après"
                  )}
                  :
                </strong>{" "}
                {t(
                  `students.form.option.${successDetails?.studentSupportCourse?.currentLearningType
                    ?.replace(/\s+/g, "")?.toLowerCase()}`,
                  successDetails?.studentSupportCourse?.currentLearningType
                )}
              </Text>
              <Text>
                <strong>
                  {t(
                    "supportCourse.student.add.form.learningState",
                    "Lieu d'apprentissage"
                  )}
                  :
                </strong>{" "}
                {t(
                  `supportCourse.student.learningState.${successDetails?.studentSupportCourse?.learningState}`,
                  successDetails?.studentSupportCourse?.learningState
                )}
              </Text>
              <Divider />
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.previousAmount",
                    "Montant précédent"
                  )}
                  :
                </strong>{" "}
                {successDetails?.payment?.previousAmount?.toLocaleString()} DZD
              </Text>
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.additionalAmount",
                    "Montant ajouté"
                  )}
                  :
                </strong>{" "}
                {successDetails?.payment?.additionalAmount?.toLocaleString()} DZD
              </Text>
              <Text>
                <strong>
                  {t(
                    "supportCourse.addSession.newTotalAmount",
                    "Nouveau total"
                  )}
                  :
                </strong>{" "}
                {successDetails?.payment?.newTotalAmount?.toLocaleString()} DZD
              </Text>
              <Divider />
              <Button type="primary" onClick={onClose}>
                {t("common.close")}
              </Button>
            </Space>
          }
        />
      )}
    </Modal>
  );
};

export default AddSessionModal;
